import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Building2, Heart, User, MapPin, Mail, Phone, Shield, Cookie, FileText, Map as SiteMap } from 'lucide-react';

const SitemapPage = () => {
  const sitemapCategories = [
    {
      title: "Pagine Principali",
      icon: Home,
      links: [
        { name: "Home", path: "/" },
        { name: "Immobili", path: "/immobili" },
        { name: "Chi Siamo", path: "/chi-siamo" },
        { name: "Vendi con Noi", path: "/vendi-con-noi" },
        { name: "Contatti", path: "/contatti" }
      ]
    },
    {
      title: "Immobili",
      icon: Building2,
      links: [
        { name: "Tutti gli Immobili", path: "/immobili" },
        { name: "Immobili in Vendita", path: "/immobili?tipo=vendita" },
        { name: "Immobili in Affitto", path: "/immobili?tipo=affitto" },
        { name: "Residenziali", path: "/immobili?propertyType=residenziale" },
        { name: "Commerciali", path: "/immobili?propertyType=commerciale" }
      ]
    },
    {
      title: "Servizi",
      icon: Heart,
      links: [
        { name: "Compravendita Immobiliare", path: "/vendi-con-noi" },
        { name: "Valutazione Gratuita", path: "/vendi-con-noi#valutazione" },
        { name: "Marketing Immobiliare", path: "/vendi-con-noi" },
        { name: "Consulenza Mutui", path: "/vendi-con-noi" }
      ]
    },
    {
      title: "Chi Siamo",
      icon: User,
      links: [
        { name: "La Nostra Storia", path: "/chi-siamo" },
        { name: "Il Team", path: "/chi-siamo" },
        { name: "I Nostri Valori", path: "/chi-siamo" }
      ]
    },
    {
      title: "Contatti",
      icon: Mail,
      links: [
        { name: "Informazioni di Contatto", path: "/contatti" },
        { name: "Dove Siamo", path: "/contatti" },
        { name: "Orari di Apertura", path: "/contatti" },
        { name: "Richiedi Informazioni", path: "/contatti" }
      ]
    },
    {
      title: "Legali",
      icon: Shield,
      links: [
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Cookie Policy", path: "/cookie-policy" },
        { name: "Termini e Condizioni", path: "/termini-condizioni" },
        { name: "Sitemap", path: "/sitemap" }
      ]
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-primary py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-monomakh mb-4">Sitemap</h1>
            <p className="text-xl text-gray-200">
              Esplora tutte le pagine e le sezioni del nostro sito web
            </p>
          </div>
        </div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4 py-12"
      >
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
          <div className="mb-8 pb-6 border-b border-gray-200">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary-dark transition-colors mb-4">
              <Home className="h-4 w-4 mr-2" />
              Torna alla Home
            </Link>
            <h2 className="text-2xl font-semibold text-primary mb-2">Mappa del Sito</h2>
            <p className="text-gray-600">
              Questa pagina contiene una panoramica completa di tutte le sezioni e pagine del nostro sito web. 
              Utilizza i link sottostanti per navigare direttamente verso la sezione desiderata.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sitemapCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4 pb-4 border-b border-gray-100">
                  <div className="bg-primary/10 p-3 rounded-full mr-3">
                    <category.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary">{category.title}</h3>
                </div>
                <ul className="space-y-2">
                  {category.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link 
                        to={link.path} 
                        className="flex items-center text-gray-600 hover:text-primary transition-colors py-1"
                      >
                        <div className="w-1.5 h-1.5 bg-secondary rounded-full mr-2"></div>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 pt-6 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-primary mb-4">Informazioni di Contatto</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Indirizzo</p>
                  <p className="text-gray-600">Via Pisana 327, 50018 Scandicci (FI)</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Telefono</p>
                  <p className="text-gray-600">+39 327 8855115</p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-gray-600">info@molinaroimmobiliare.it</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SitemapPage;